// ─── ThirdPartyDDPageEnhanced.jsx ─────────────────────────────────────────────
// Enhanced Third Party Due Diligence Page
// Integrates questionnaires, external research, and client sharing
// ────────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "./theme";
import { useAuditLog } from "./useAuditLog";
import { downloadCSV } from "./exportUtils";
import { db } from "./dbClient";
import ThirdPartyDDQuestionnaireForm from "./ThirdPartyDDQuestionnaireForm";

const ENTITY_TYPES = [
  "payment_processor",
  "correspondent_bank",
  "vasp",
  "service_provider",
  "vendor",
  "partner",
  "other",
];
const REL_TYPES = ["ongoing", "one_time", "pending"];
const RISK_RATINGS = ["low", "medium", "high", "critical"];
const STATUSES = ["pending_review", "under_review", "approved", "rejected", "expired"];
const REVIEW_MONTHS = { low: 24, medium: 12, high: 6, critical: 3 };
const RISK_COLOR = {
  low: "#39FF14",
  medium: "#F59E0B",
  high: "#FF8C00",
  critical: "#FF2244",
};
const STATUS_COLOR = {
  pending_review: "#F59E0B",
  under_review: "#A855F7",
  approved: "#39FF14",
  rejected: "#FF2244",
  expired: "#4A5870",
};

const emptyForm = () => ({
  entity_name: "",
  entity_type: "vendor",
  relationship_type: "ongoing",
  jurisdiction: "",
  risk_rating: "medium",
  notes: "",
  assigned_to: "",
});

export default function ThirdPartyDDPageEnhanced({ company, currentUser, team = [] }) {
  const C = useTheme();
  const { t } = useTranslation();
  const { log } = useAuditLog(company, currentUser);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [selected, setSelected] = useState(null);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState(null);
  const [questionnaires, setQuestionnaires] = useState({});
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterRisk, setFilterRisk] = useState("all");
  const [saving, setSaving] = useState(false);
  const [showGenerateLink, setShowGenerateLink] = useState(null);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);

  useEffect(() => {
    if (company?.id) load();
  }, [company?.id]);

  async function load() {
    setLoading(true);
    try {
      // Load third party DD records
      const { data: tpdd } = await db
        .from("third_party_dd")
        .select("*")
        .eq("company_id", company.id)
        .order("created_at", false)
        .execute();

      setRecords(tpdd || []);

      // Load questionnaires for each record
      if (tpdd?.length > 0) {
        const { data: questData } = await db
          .from("third_party_dd_questionnaires")
          .select("*")
          .in(
            "third_party_dd_id",
            tpdd.map(r => r.id)
          )
          .execute();

        if (questData) {
          const questMap = {};
          questData.forEach(q => {
            questMap[q.third_party_dd_id] = q;
          });
          setQuestionnaires(questMap);
        }
      }
    } catch (err) {
      console.error("Error loading data:", err);
    }
    setLoading(false);
  }

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);

    const months = REVIEW_MONTHS[form.risk_rating] || 12;
    const nextReview = new Date();
    nextReview.setMonth(nextReview.getMonth() + months);

    try {
      const payload = {
        ...form,
        assigned_to: form.assigned_to || null,
        company_id: company.id,
        status: "pending_review",
        screening_date: new Date().toISOString().slice(0, 10),
        next_review_date: nextReview.toISOString().slice(0, 10),
        created_by: currentUser?.id,
      };

      const { data, error } = await db
        .from("third_party_dd")
        .insert(payload)
        .execute();

      if (error) {
        console.error("Error creating record:", error);
        alert(`Failed to create third party: ${error.message}`);
      } else if (data) {
        const newRecord = Array.isArray(data) ? data[0] : data;
        setRecords(p => [newRecord, ...p]);
        await log({
          action: "third_party_dd_created",
          module: "third_party_dd",
          entityId: String(newRecord.id),
          entityName: form.entity_name,
          meta: { risk: form.risk_rating, type: form.entity_type },
        });
        setShowForm(false);
        setForm(emptyForm());
      }
    } catch (err) {
      console.error("Error creating record:", err);
      alert(`Error creating third party: ${err.message}`);
    }
    setSaving(false);
  }

  async function generateShareableLink(recordId, entityName) {
    setSaving(true);
    try {
      const token = Array.from(crypto.getRandomValues(new Uint8Array(24)))
        .map(b => b.toString(16).padStart(2, "0"))
        .join("");

      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 30); // 30 days

      // Generate proper UUID v4 on frontend
      const linkId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });

      const { data, error } = await db
        .from("dd_questionnaire_links")
        .insert({
          id: linkId,
          third_party_dd_id: recordId,
          company_id: company.id,
          token,
          entity_name: entityName,
          expires_at: expiresAt.toISOString(),
        })
        .execute();

      if (error) {
        alert(`Failed to generate link: ${error.message}`);
        console.error("Error generating link:", error);
      } else {
        const baseUrl = window.location.origin;
        // Use /app path since root redirects to marketing site
        const linkUrl = `${baseUrl}/app?ddq=${token}`;
        setGeneratedLink(linkUrl);
        await log({
          action: "dd_link_generated",
          module: "third_party_dd",
          entityId: String(recordId),
          entityName,
          meta: { token, expiresAt: expiresAt.toISOString() },
        });
      }
    } catch (err) {
      alert(`Error generating link: ${err.message}`);
      console.error("Error generating link:", err);
    }
    setSaving(false);
  }

  async function updateStatus(id, status, extra = {}) {
    const rec = records.find(r => r.id === id);

    try {
      const { data, error } = await db
        .from("third_party_dd")
        .update({
          status,
          ...extra,
          approval_date: ["approved", "rejected"].includes(status)
            ? new Date().toISOString().slice(0, 10)
            : undefined,
          approved_by: ["approved", "rejected"].includes(status)
            ? `${currentUser?.firstName} ${currentUser?.lastName}`
            : undefined,
        })
        .eq("id", id)
        .execute();

      if (error) {
        alert(`Failed to update status: ${error.message}`);
        console.error("Error updating status:", error);
        return;
      }

      if (data) {
        const updated = Array.isArray(data) ? data[0] : data;
        setRecords(p => p.map(r => (r.id === id ? updated : r)));
        if (selected?.id === id) setSelected(updated);
        alert(`Status updated to ${status}`);
      }

      await log({
        action: `third_party_dd_${status}`,
        module: "third_party_dd",
        entityId: String(id),
        entityName: rec?.entity_name || id,
        newValue: { status },
      });
    } catch (err) {
      alert(`Error updating status: ${err.message}`);
      console.error("Error updating status:", err);
    }
  }

  const filtered = records.filter(
    r =>
      (filterStatus === "all" || r.status === filterStatus) &&
      (filterRisk === "all" || r.risk_rating === filterRisk)
  );

  const dueReview = records.filter(
    r =>
      r.next_review_date &&
      new Date(r.next_review_date) <= new Date() &&
      r.status === "approved"
  );

  const highRisk = records.filter(
    r =>
      ["high", "critical"].includes(r.risk_rating) && r.status === "approved"
  );

  const S = {
    card: {
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 10,
      padding: 20,
    },
    btn: (p) => ({
      background: p ? C.accent : "transparent",
      border: `1px solid ${p ? C.accent : C.border}`,
      borderRadius: 7,
      color: p ? C.bg : C.textMid,
      padding: "7px 16px",
      cursor: "pointer",
      fontSize: 12,
      fontFamily: "'DM Mono',monospace",
    }),
    input: {
      background: C.bg,
      border: `1px solid ${C.border}`,
      borderRadius: 6,
      color: C.text,
      padding: "7px 10px",
      fontSize: 12,
      fontFamily: "'DM Mono',monospace",
      width: "100%",
      boxSizing: "border-box",
    },
    label: { color: C.textMid, fontSize: 11, display: "block", marginBottom: 4 },
  };

  if (showQuestionnaire && selected) {
    return (
      <div style={S.card}>
        <ThirdPartyDDQuestionnaireForm
          thirdPartyId={selected.id}
          entityName={selected.entity_name}
          company={company}
          currentUser={currentUser}
          initialData={questionnaires[selected.id]}
          onSave={() => {
            load();
            setShowQuestionnaire(false);
          }}
          onClose={() => setShowQuestionnaire(false)}
        />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h1 style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 24, fontWeight: 700, margin: 0 }}>
            Third Party Due Diligence
          </h1>
          <p style={{ color: C.textMid, margin: "4px 0 0", fontSize: 13 }}>
            Manage vendor, partner, and service provider assessments
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => downloadCSV(records, `third_party_dd_${Date.now()}.csv`)} style={S.btn(false)}>
            📥 Export CSV
          </button>
          <button onClick={() => setShowForm(true)} style={S.btn(true)}>
            + Add Third Party
          </button>
        </div>
      </div>

      {dueReview.length > 0 && (
        <div
          style={{
            background: `${C.warn}18`,
            border: `1px solid ${C.warn}`,
            borderRadius: 8,
            padding: "10px 16px",
            marginBottom: 16,
            color: C.warn,
            fontSize: 13,
          }}
        >
          ⚠ {dueReview.length} third-party relationship{dueReview.length > 1 ? "s are" : " is"} due for review.
        </div>
      )}

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 20 }}>
        {[
          {
            label: "Total Active",
            value: records.filter(r => r.status === "approved").length,
            color: C.accent,
          },
          { label: "High/Critical Risk", value: highRisk.length, color: C.danger },
          { label: "Due for Review", value: dueReview.length, color: C.warn },
          {
            label: "With Questionnaire",
            value: Object.keys(questionnaires).length,
            color: C.success,
          },
        ].map(k => (
          <div key={k.label} style={{ ...S.card, borderTop: `2px solid ${k.color}`, textAlign: "center" }}>
            <div style={{ color: k.color, fontSize: 28, fontFamily: "'Syne',sans-serif", fontWeight: 700 }}>
              {k.value}
            </div>
            <div style={{ color: C.textMid, fontSize: 11 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {["all", ...STATUSES].map(s => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            style={{
              ...S.btn(filterStatus === s),
              padding: "4px 12px",
              fontSize: 11,
            }}
          >
            {s === "all" ? "All" : s.replace(/_/g, " ")}
          </button>
        ))}
        <div style={{ width: 1, background: C.border }} />
        {["all", ...RISK_RATINGS].map(r => (
          <button
            key={r}
            onClick={() => setFilterRisk(r)}
            style={{
              ...S.btn(filterRisk === r),
              padding: "4px 12px",
              fontSize: 11,
            }}
          >
            {r === "all" ? "All Risks" : r.charAt(0).toUpperCase() + r.slice(1)}
          </button>
        ))}
      </div>

      <div style={S.card}>
        {loading ? (
          <div style={{ color: C.textDim, textAlign: "center", padding: 32 }}>
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ color: C.textDim, textAlign: "center", padding: 32 }}>
            No third-party records found.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                {["Entity", "Type", "Jurisdiction", "Risk", "Status", "Questionnaire", "Actions"].map(h => (
                  <th
                    key={h}
                    style={{
                      color: C.textDim,
                      padding: "8px 10px",
                      textAlign: "left",
                      borderBottom: `1px solid ${C.border}`,
                      fontSize: 11,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const quest = questionnaires[r.id];
                const overdue =
                  r.next_review_date &&
                  new Date(r.next_review_date) < new Date() &&
                  r.status === "approved";

                return (
                  <tr
                    key={r.id}
                    onClick={() => setSelected(r)}
                    style={{
                      cursor: "pointer",
                      borderBottom: `1px solid ${C.border}`,
                      background: overdue ? `${C.warn}11` : "transparent",
                    }}
                  >
                    <td style={{ padding: "10px", color: C.text, fontWeight: 600 }}>
                      {r.entity_name}
                    </td>
                    <td style={{ padding: "10px", color: C.textMid }}>
                      {r.entity_type?.replace(/_/g, " ")}
                    </td>
                    <td style={{ padding: "10px", color: C.textMid }}>
                      {r.jurisdiction || "—"}
                    </td>
                    <td style={{ padding: "10px" }}>
                      <span
                        style={{
                          fontSize: 10,
                          padding: "2px 7px",
                          borderRadius: 10,
                          background: `${RISK_COLOR[r.risk_rating]}22`,
                          color: RISK_COLOR[r.risk_rating],
                          fontWeight: 700,
                        }}
                      >
                        {r.risk_rating}
                      </span>
                    </td>
                    <td style={{ padding: "10px" }}>
                      <span
                        style={{
                          fontSize: 10,
                          padding: "2px 7px",
                          borderRadius: 10,
                          background: `${STATUS_COLOR[r.status]}22`,
                          color: STATUS_COLOR[r.status],
                        }}
                      >
                        {r.status?.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td style={{ padding: "10px" }}>
                      {quest ? (
                        <span
                          style={{
                            fontSize: 10,
                            padding: "2px 7px",
                            borderRadius: 10,
                            background: `${C.success}22`,
                            color: C.success,
                            fontWeight: 700,
                          }}
                        >
                          ✓ Score: {quest.risk_score}
                        </span>
                      ) : (
                        <span style={{ fontSize: 10, color: C.textDim }}>
                          —
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "10px", display: "flex", gap: 4 }}>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          setSelected(r);
                          setShowQuestionnaire(true);
                        }}
                        style={{ ...S.btn(false), padding: "3px 8px", fontSize: 10 }}
                      >
                        📋
                      </button>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          setShowGenerateLink(r.id);
                        }}
                        style={{ ...S.btn(false), padding: "3px 8px", fontSize: 10 }}
                      >
                        🔗
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Modal */}
      {selected && !showQuestionnaire && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setSelected(null)}
        >
          <div
            style={{ ...S.card, maxWidth: 500, width: "90%", maxHeight: "80vh", overflowY: "auto" }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>
                {selected.entity_name}
              </div>
              <button
                onClick={() => setSelected(null)}
                style={{ background: "transparent", border: "none", color: C.textDim, cursor: "pointer", fontSize: 20 }}
              >
                ×
              </button>
            </div>

            {[
              ["Entity Type", selected.entity_type?.replace(/_/g, " ")],
              ["Relationship", selected.relationship_type],
              ["Jurisdiction", selected.jurisdiction],
              ["Risk Rating", selected.risk_rating],
              ["Status", selected.status?.replace(/_/g, " ")],
              ["Screening Date", selected.screening_date],
              ["Next Review", selected.next_review_date],
            ].map(([k, v]) =>
              v ? (
                <div key={k} style={{ marginBottom: 10 }}>
                  <div style={{ color: C.textDim, fontSize: 10, marginBottom: 2 }}>
                    {k.toUpperCase()}
                  </div>
                  <div style={{ color: C.text, fontSize: 12 }}>{v}</div>
                </div>
              ) : null
            )}

            {questionnaires[selected.id] && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
                <div style={{ color: C.textDim, fontSize: 10, marginBottom: 8 }}>
                  QUESTIONNAIRE SCORE
                </div>
                <div style={{ color: C.text, fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
                  {questionnaires[selected.id].risk_score} / 100
                </div>
                <button
                  onClick={() => {
                    setShowQuestionnaire(true);
                  }}
                  style={S.btn(true)}
                >
                  View Questionnaire
                </button>
              </div>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              {selected.status !== "approved" && (
                <button onClick={() => updateStatus(selected.id, "approved")} style={S.btn(true)}>
                  Approve
                </button>
              )}
              {selected.status !== "rejected" && (
                <button onClick={() => updateStatus(selected.id, "rejected")} style={S.btn(false)}>
                  Reject
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Generate Link Modal */}
      {showGenerateLink && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => {
            setShowGenerateLink(null);
            setGeneratedLink(null);
          }}
        >
          <div
            style={{ ...S.card, maxWidth: 400, width: "90%" }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ color: C.text, margin: 0, marginBottom: 16 }}>
              Share Questionnaire
            </h3>

            {!generatedLink ? (
              <button
                onClick={() => generateShareableLink(showGenerateLink, records.find(r => r.id === showGenerateLink)?.entity_name)}
                disabled={saving}
                style={S.btn(true)}
              >
                {saving ? "Generating…" : "Generate Secure Link"}
              </button>
            ) : (
              <div>
                <p style={{ color: C.textMid, fontSize: 12, marginBottom: 12 }}>
                  Share this link with the entity to complete their DD questionnaire:
                </p>
                <div
                  style={{
                    background: C.bg,
                    border: `1px solid ${C.border}`,
                    borderRadius: 6,
                    padding: 10,
                    marginBottom: 12,
                    fontSize: 11,
                    wordBreak: "break-all",
                    color: C.text,
                    fontFamily: "'DM Mono', monospace",
                  }}
                >
                  {generatedLink}
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(generatedLink);
                    alert("Link copied to clipboard!");
                  }}
                  style={S.btn(true)}
                >
                  Copy Link
                </button>
              </div>
            )}

            <button
              onClick={() => {
                setShowGenerateLink(null);
                setGeneratedLink(null);
              }}
              style={{ ...S.btn(false), marginTop: 12, width: "100%" }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Create Modal (same as original) */}
      {showForm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowForm(false)}
        >
          <div
            style={{ ...S.card, maxWidth: 520, width: "90%", maxHeight: "85vh", overflowY: "auto" }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ color: C.text, fontWeight: 700, fontSize: 16, marginBottom: 16 }}>
              Add Third Party
            </div>
            <form onSubmit={handleCreate}>
              <div style={{ marginBottom: 12 }}>
                <label style={S.label}>Entity Name *</label>
                <input
                  required
                  value={form.entity_name}
                  onChange={e => setForm(p => ({ ...p, entity_name: e.target.value }))}
                  style={S.input}
                />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={S.label}>Entity Type</label>
                  <select
                    value={form.entity_type}
                    onChange={e => setForm(p => ({ ...p, entity_type: e.target.value }))}
                    style={S.input}
                  >
                    {ENTITY_TYPES.map(t => (
                      <option key={t} value={t}>
                        {t.replace(/_/g, " ")}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={S.label}>Risk Rating</label>
                  <select
                    value={form.risk_rating}
                    onChange={e => setForm(p => ({ ...p, risk_rating: e.target.value }))}
                    style={S.input}
                  >
                    {RISK_RATINGS.map(r => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
                <button type="button" onClick={() => setShowForm(false)} style={S.btn(false)}>
                  Cancel
                </button>
                <button type="submit" disabled={saving} style={S.btn(true)}>
                  {saving ? "Saving…" : "Add Third Party"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
